import { Box, Button, Flex, Text } from "@simplybusiness/mobius";
import "./PricingNudge.css";

export type PricingNudgeCopyVariant = "default" | "variantA" | "variantB";

type PricingNudgeProps = {
  //Called when the price estimate button is clicked
  onPriceEstimateButtonClickedCallback: () => void;
  vertical?: string;
  copyVariant?: PricingNudgeCopyVariant;
};

const getHeadingCopy = (
  copyVariant: PricingNudgeCopyVariant,
  vertical?: string,
) => {
  const questionCount = vertical === "landlord" ? "five" : "four";

  switch (copyVariant) {
    case "variantA":
      return {
        testId: "pricing-nudge-variant-a-heading",
        className: "mobius-interventions__pricing-nudge-text-large",
        text: (
          <>
            Just want an
            <span className="mobius-interventions__pricing-nudge-emphasized-text">
              {" "}
              idea of price?{" "}
            </span>
            <br />
            Answer {questionCount} simple questions.
          </>
        ),
      };
    case "variantB":
      return {
        testId: "pricing-nudge-variant-b-heading",
        className: "mobius-interventions__pricing-nudge-text",
        text: (
          <>
            Want to
            <span className="mobius-interventions__pricing-nudge-emphasized-text">
              {" "}
              quickly{" "}
            </span>
            see how we compare on price?
          </>
        ),
      };
    case "default":
    default:
      return {
        testId: "pricing-nudge-default-heading",
        className: "mobius-interventions__pricing-nudge-text",
        text: (
          <>
            Want an
            <span className="mobius-interventions__pricing-nudge-emphasized-text">
              {" "}
              idea of price{" "}
            </span>
            without needing to answer a lot of questions?
          </>
        ),
      };
  }
};

const getSubtextCopy = (
  copyVariant: PricingNudgeCopyVariant,
  vertical?: string,
) => {
  const questionCount = vertical === "landlord" ? "five" : "four";

  switch (copyVariant) {
    case "variantA":
      return {
        testId: "pricing-nudge-variant-a-subtext",
        text: "",
      };
    case "variantB":
      return {
        testId: "pricing-nudge-variant-b-subtext",
        text: `Answer ${questionCount} quick questions and we'll let you know what your price could be, no personal details required.`,
      };
    case "default":
    default:
      return {
        testId: "pricing-nudge-default-subtext",
        text: `Answer ${questionCount} quick questions and we'll let you know what your price could be.`,
      };
  }
};

const getCtaCopy = (copyVariant: PricingNudgeCopyVariant): string => {
  switch (copyVariant) {
    case "variantA":
    case "variantB":
      return "Get a price estimate";
    default:
      return "Get price estimate";
  }
};

export const PricingNudge = (props: PricingNudgeProps) => {
  const { vertical, copyVariant = "default" } = props;
  const headingCopy = getHeadingCopy(copyVariant, vertical);
  const subtextCopy = getSubtextCopy(copyVariant, vertical);
  const ctaCopy = getCtaCopy(copyVariant);

  return (
    <Box className="mobius-interventions__pricing-nudge">
      <Flex flexDirection="column" justifyContent="center">
        <Text className={headingCopy.className} elementType="h1">
          <span data-testid={headingCopy.testId}>{headingCopy.text}</span>
        </Text>

        <Text
          elementType="p"
          className="mobius-interventions__pricing-nudge-subtext"
        >
          <span data-testid={subtextCopy.testId}>{subtextCopy.text}</span>
        </Text>
      </Flex>

      <Flex rowGap="lg" columnGap="lg" justifyContent="center">
        <Button
          className="mobius-interventions__pricing-nudge-cta"
          variant="primary"
          size="sm"
          onClick={props.onPriceEstimateButtonClickedCallback}
        >
          {ctaCopy}
        </Button>
      </Flex>
    </Box>
  );
};
